/**
 * Curated textpath helpers — parse a layout slot's authored `textpath` array
 * and wrap a phrase across those guides, matching platform's aggregate-textpath
 * generator (tapgen/src/generate: createAggregateTextpath + lo_slots).
 *
 * Each entry in a slot's `textpath` array is a guide path for one line of text,
 * authored in the layout coordinate space (viewBox 1800 × 1800*heightRatio).
 * Text wraps across the first N guides (one line per guide), each line fit to
 * its guide's length. We render the guides verbatim in an SVG with that viewBox
 * so positions match the background exactly (see CuratedText).
 */

// Font-size tiers, mirroring tapgen layoutElements.fontSizes (viewBox px) and
// the h1/h2/h3 sizes from assignFontSize (h2 = lerp(44, 150, 0.15)).
const FONT_TIERS = {
  title: 64,
  word: 72,
  outtake: 60,
  sub: 54,
  t1: 50,
  t2: 42,
  t3: 32,
  caption: 28,
  h1: 150,
  h2: 60,
  h3: 44,
};
const DEFAULT_FONT_SIZE = 44;

// Fraction of a guide's length text is allowed to occupy. The leftover (~8%)
// leaves the left-anchored text a little room before its trailing glyph reaches
// the path end. Applied to both wrapping and the render-time textLength clamp so
// the two agree.
export const TEXTPATH_FIT = 0.92;

export function resolveFontSize(hint) {
  if (hint == null) return DEFAULT_FONT_SIZE;
  if (Object.prototype.hasOwnProperty.call(FONT_TIERS, hint)) return FONT_TIERS[hint];
  const n = parseFloat(hint);
  return Number.isFinite(n) ? n : DEFAULT_FONT_SIZE;
}

// Line-height multiplier for stacked lines on a single textpath, mirroring
// tapgen layoutElements: scaleToRange(fontSize, 15, 56.25, 1, 0.8). Clamped.
const MAX_FONT_SIZE = 150;
export function lineHeightScalar(fontSize) {
  const inMin = MAX_FONT_SIZE * 0.1;
  const inMax = MAX_FONT_SIZE * 0.375;
  const s = ((fontSize - inMin) * (0.8 - 1)) / (inMax - inMin) + 1;
  return Math.max(0.8, Math.min(1.2, s));
}

function nums(str) {
  return (str.match(/-?\d*\.?\d+(?:e-?\d+)?/g) || []).map(Number);
}

function attr(str, name) {
  const m = str.match(new RegExp(`${name}\\s*=\\s*"([^"]*)"`, 'i'));
  return m ? m[1] : null;
}

function dist(ax, ay, bx, by) {
  return Math.hypot(bx - ax, by - ay);
}

// Approximate length of a path d that is either "M..L.." or "M..Q.." (the two
// shapes tapgen emits for guides). Quadratics are sampled; anything else falls
// back to the chord between first and last point.
function pathLength(d) {
  const upper = d.toUpperCase();
  const pts = nums(d);
  if (pts.length < 4) return 0;
  if (upper.includes('Q') && pts.length >= 6) {
    const [x0, y0, cx, cy, x1, y1] = pts;
    let len = 0;
    let px = x0;
    let py = y0;
    const STEPS = 24;
    for (let i = 1; i <= STEPS; i++) {
      const t = i / STEPS;
      const mt = 1 - t;
      const x = mt * mt * x0 + 2 * mt * t * cx + t * t * x1;
      const y = mt * mt * y0 + 2 * mt * t * cy + t * t * y1;
      len += dist(px, py, x, y);
      px = x;
      py = y;
    }
    return len;
  }
  // line / generic: chord between first and last point
  return dist(pts[0], pts[1], pts[pts.length - 2], pts[pts.length - 1]);
}

/**
 * Parse a slot.textpath into renderable guides. Accepts either an array (an
 * "aggregate" textpath — one guide per line) or a single string (a textpath
 * used `textpathData.lines` times, stacked by the caller). `d` is rendered
 * verbatim (the browser handles any path type); `length` is a fallback — the
 * renderer prefers the real getTotalLength of the mounted path.
 * @returns {Array<{ d: string, length: number }>}
 */
export function parseGuides(textpath) {
  const arr = Array.isArray(textpath) ? textpath : textpath ? [textpath] : [];
  const guides = [];
  for (const raw of arr) {
    if (typeof raw !== 'string') continue;
    if (/<line/i.test(raw)) {
      const x1 = parseFloat(attr(raw, 'x1'));
      const y1 = parseFloat(attr(raw, 'y1'));
      const x2 = parseFloat(attr(raw, 'x2'));
      const y2 = parseFloat(attr(raw, 'y2'));
      if ([x1, y1, x2, y2].every(Number.isFinite)) {
        guides.push({ d: `M ${x1} ${y1} L ${x2} ${y2}`, length: dist(x1, y1, x2, y2) });
      }
    } else {
      const d = attr(raw, 'd');
      if (d) guides.push({ d, length: pathLength(d) });
    }
  }
  return guides;
}

/**
 * Wrap a phrase across guides (one line per guide), balanced so no single line
 * is dramatically over-stuffed. We pick the fewest lines that fit at full size,
 * then minimize the worst line's fill ratio (text width / guide length) across
 * those lines — instead of greedily filling early lines and cramming the
 * remainder onto the last guide. `measure(text, fontSize)` returns a pixel width
 * in the same (viewBox) units as the guide lengths.
 *
 * @returns {Array<{ d: string, text: string, length: number }>} one per used guide
 */
export function wrapAcrossGuides(phrase, guides, fontSize, measure) {
  const words = (phrase || '').trim().split(/\s+/).filter(Boolean);
  if (!words.length || !guides.length) return [];

  const lengths = guides.map((g) => g.length || 1);
  // Per-word widths (incl. trailing space, like tapgen) → cumulative prefix so
  // a line's width is an O(1) difference.
  const wordW = words.map((w, i) => measure(w + (i < words.length - 1 ? ' ' : ''), fontSize));
  const cum = [0];
  for (let i = 0; i < words.length; i++) cum.push(cum[i] + wordW[i]);
  const lineW = (a, b) => cum[b] - cum[a]; // words [a, b)

  // Greedy fill into at most L lines with per-line cap = ratio*length; returns
  // the word-break boundaries if everything fits in L lines, else null. Each
  // line takes at least one word (a too-long word is forced, never split).
  const tryFill = (ratio, L) => {
    const breaks = [0];
    let wi = 0;
    for (let line = 0; line < L && wi < words.length; line++) {
      const cap = ratio * lengths[line] * TEXTPATH_FIT;
      let end = wi + 1;
      while (end < words.length && lineW(wi, end + 1) <= cap) end++;
      wi = end;
      breaks.push(wi);
    }
    return wi >= words.length ? breaks : null;
  };

  // Fewest lines that fit at full size, then balance across exactly those lines.
  let L = guides.length;
  for (let l = 1; l <= guides.length; l++) {
    if (tryFill(1.0, l)) {
      L = l;
      break;
    }
  }
  let lo = 0.15;
  let hi = 4.0; // a high ratio always fits (one line absorbs everything)
  for (let it = 0; it < 18; it++) {
    const mid = (lo + hi) / 2;
    if (tryFill(mid, L)) hi = mid;
    else lo = mid;
  }
  const breaks = tryFill(hi, L) || tryFill(1.0, guides.length) || [0, words.length];

  const lines = [];
  for (let i = 0; i + 1 < breaks.length; i++) {
    const a = breaks[i];
    const b = breaks[i + 1];
    if (a >= b) continue;
    const gi = Math.min(i, guides.length - 1);
    lines.push({ d: guides[gi].d, text: words.slice(a, b).join(' '), length: guides[gi].length });
  }
  return lines;
}
